import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Crown, Check, Infinity as InfinityIcon, Sparkles, Users, Waves } from "lucide-react";

const benefits = [
  { icon: InfinityIcon, title: "Unlimited companion conversations", text: "Reflect with your Jungian companion as often as you need — no daily limit." },
  { icon: Sparkles, title: "Full depth-tools suite", text: "Every shadow-work, active-imagination and guided practice, unlocked." },
  { icon: Waves, title: "Complete frequency & meditation library", text: "All healing frequencies and guided meditations, always available." },
  { icon: Users, title: "Priority therapist matching", text: "Front-of-line appointment requests with culturally-matched therapists." },
];

const Plus = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const isPlus = user?.plan === "plus";

  const subscribe = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: "aurea_plus_monthly",
        origin_url: window.location.origin,
        user_id: user?.id,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="plus-page">
      <div className="text-center mb-10">
        <span className="inline-flex items-center gap-2 rounded-full bg-accent/10 text-accent px-4 py-1.5 text-xs font-medium tracking-wide uppercase mb-5">
          <Crown className="w-4 h-4" /> {isPlus ? "You're a member" : "Membership"}
        </span>
        <h1 className="font-serif text-4xl sm:text-5xl text-ink tracking-tight">Aurea Plus</h1>
        <p className="mt-4 text-ink-2 max-w-xl mx-auto leading-relaxed">
          Go deeper, without limits. Aurea Plus opens every tool and unlimited time with your companion —
          so your healing is never rationed.
        </p>
      </div>

      <div className="bg-white rounded-3xl border border-linen-3 p-8 sm:p-10">
        <div className="flex items-end gap-2 mb-8">
          <span className="font-serif text-5xl text-brand" data-testid="plus-price">$12.99</span>
          <span className="text-ink-mute mb-2">/ month</span>
        </div>

        <ul className="space-y-5 mb-9">
          {benefits.map((b, i) => (
            <li key={i} className="flex gap-4" data-testid={`plus-benefit-${i}`}>
              <span className="w-10 h-10 rounded-2xl bg-calm flex items-center justify-center shrink-0">
                <b.icon className="w-5 h-5 text-brand" />
              </span>
              <div>
                <p className="text-ink font-medium">{b.title}</p>
                <p className="text-ink-2 text-sm leading-relaxed">{b.text}</p>
              </div>
            </li>
          ))}
        </ul>

        {isPlus ? (
          <div className="flex items-center justify-center gap-2 rounded-full bg-calm text-calm-text py-4 font-medium" data-testid="plus-active">
            <Check className="w-5 h-5" /> Your membership is active. Thank you.
          </div>
        ) : (
          <>
            <Button onClick={subscribe} disabled={loading} className="w-full rounded-full bg-brand hover:bg-brand-hover text-linen h-14 text-base" data-testid="plus-subscribe">
              {loading ? "Preparing secure checkout…" : "Upgrade to Aurea Plus"}
            </Button>
            <p className="text-center text-xs text-ink-mute mt-4">
              Secure payment by Stripe · Cancel anytime · Billed monthly
            </p>
          </>
        )}
      </div>

      <p className="text-center text-xs text-ink-mute mt-6 max-w-lg mx-auto">
        The free plan always stays free, including journaling, mood tracking, crisis safety net and
        {" "}{"a daily allowance of companion reflections."}
      </p>
    </div>
  );
};

export default Plus;
